import React, { useEffect, useState, useMemo, createContext, useContext, useReducer } from 'react';

import { totalCountEvent } from 'services/generalServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { getTenantDetails } from 'utils/utils';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getPracticeAreas } from 'services/CaseServices';
import automationRulesLocalReducer, { initialAutomationState } from '../reducers/workFlowAutomationLocalReducer';
import { listUsers } from 'services/teamManagement.service';
import { generateFormattedFirmUsers } from '../../helpers/automationCommonHelpers';

const AutomationContext = createContext();

const useAutomationContext = () => useContext(AutomationContext);

const AutomationContextProvider = (props) => {
  const { children } = props || {};

  const { addToast } = useToast();

  const websocketContext = useContext(WebSocketConnectionContext);

  const userContext = useContext(UserDetailsContext);

  const [workflowAutomationStateData, dispatch] = useReducer(automationRulesLocalReducer, initialAutomationState);

  const [tenantId, tenantSk] = getTenantDetails();
  const [practiceAreaOptions, setPracticeAreaOptions] = useState([]);
  const [firmUsersList, setFirmUsersList] = useState([]);
  const [firmUsersOnlyList, setFirmUsersOnlyList] = useState([]);
  const [isUpdateScreen, setIsUpdateScreen] = useState(false);
  const [isAutomationErrorLogScreen, setIsAutomationErrorLogScreen] = useState(false);

  const [triggerName, setTriggerName] = useState('');
  const [isForLeadAutomation, setIsForLeadAutomation] = useState(false); //For which module this trigger is gonna be appplied (Leads or Cases)

  const [filterDropdownOptions, setFilterDropdownOptions] = useState([]);
  const [isPracticeAreaLoading, setIsPracticeAreaLoading] = useState(false);

  /** --- fucntion to fetch all active firm-users --- **/
  const loadUserList = () => {
    totalCountEvent('user')
      .then((response) => {
        //paylod data
        let status = !isUpdateScreen ? 'ACTIVE' : 'all';
        let pageLimit = response?.data || 10;
        let paginationToken = '';
        let paginationDirection = 'forward';
        let keyWord = '';
        let isIncludeContacts = false;
        let isDetailed = false;
        let isConfirmedUser = !isUpdateScreen ? true : false;
        const isIncludeGroups = true;
        const active_case_count = false;
        let active_groups = !isUpdateScreen ? true : false;

        listUsers(
          status,
          pageLimit,
          paginationToken,
          paginationDirection,
          keyWord,
          isIncludeContacts,
          isDetailed,
          isConfirmedUser,
          isIncludeGroups,
          active_case_count,
          active_groups
        ).then((res) => {
          let firmUsers = res?.data?.firm_users || [];
          if (res?.data?.firm_users?.length) {
            setFirmUsersList(firmUsers);
          }
        });
      })
      .catch((error) => {
        console.log('error', error);
      });
  };

  /* --- fetching practice-area lists --- */
  const getPracticeAreasList = async () => {
    try {
      setIsPracticeAreaLoading(true);
      let totoalCountResponse = await totalCountEvent('practice-area');

      if (totoalCountResponse?.data) {
        let pageLimit = totoalCountResponse?.data;
        const filters = {
          status: 'all',
        };
        let response = await getPracticeAreas(tenantId, tenantSk, pageLimit, '', 'forward', isUpdateScreen && filters, true);
        let practiceAreasList = [];

        if (response?.data) {
          let practiceAreaData = response?.data?.['practice-area'] || [];

          if (practiceAreaData?.length > 0) {
            practiceAreaData?.forEach((val) => {
              const { practice_area_name, sk, status, practice_area_id } = val || {};
              practiceAreasList.push({
                sk,
                label: practice_area_name,
                value: practice_area_id,
                status: status,
              });
            });
          }
        }

        setPracticeAreaOptions(practiceAreasList);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsPracticeAreaLoading(false);
    }
  };

  /**--- Function to fetch all contact type list including primary contacts ,billing contacts and firm users --- **/
  const getAllRequiredContactsList = (firmUsersList = []) => {
    let contactsList = [];

    // let formattedFirmUsersList = generateFormattedFirmUsers(firmUsersList, triggerName, isForLeadAutomation) || [];
    let formattedFirmUsersList =
      generateFormattedFirmUsers({ firmUsersList, triggerName, isForLeadAutomation, setFirmUsersOnlyList }) || [];

    if (formattedFirmUsersList?.length) {
      contactsList = [...formattedFirmUsersList];
    }

    return contactsList;
  };

  useEffect(() => {
    getPracticeAreasList();
    loadUserList();
  }, [isUpdateScreen, isAutomationErrorLogScreen]);

  const contextPayload = useMemo(() => {
    const globalAutomationData = workflowAutomationStateData?.globalData || {};

    return {
      addToast,
      tenantId,
      tenantSk,
      websocketContext,
      userContext,
      toastConstant,
      constants,
      practiceAreaOptions,
      firmUsersList,
      totalCountEvent,
      generateFullName,
      globalAutomationData,
      dispatch,
      getAllRequiredContactsList,
      triggerName,
      setTriggerName,
      filterDropdownOptions,
      setFilterDropdownOptions,
      isForLeadAutomation,
      setIsForLeadAutomation,
      firmUsersOnlyList,
      isUpdateScreen,
      setIsUpdateScreen,
      isAutomationErrorLogScreen,
      setIsAutomationErrorLogScreen,
      isPracticeAreaLoading,
    };
  }, [
    workflowAutomationStateData?.globalData,
    addToast,
    tenantId,
    tenantSk,
    websocketContext,
    userContext,
    practiceAreaOptions,
    firmUsersList,
    triggerName,
    filterDropdownOptions,
    isForLeadAutomation,
    firmUsersOnlyList,
    isUpdateScreen,
    setIsUpdateScreen,
    isAutomationErrorLogScreen,
    setIsAutomationErrorLogScreen,
    dispatch,
    isPracticeAreaLoading,
  ]);

  return <AutomationContext.Provider value={contextPayload}>{children}</AutomationContext.Provider>;
};

//export statement
export { AutomationContext, useAutomationContext, AutomationContextProvider };
